import { useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { useProduct, useUpdateProduct } from '../../hooks/useProducts';
import { ProductForm } from '@modules/products';
import { Modal } from '@shared/ui';
import { useCategories } from '@modules/categories';
import type { CategorySelectionItem } from '../CategorySelection';
import { Loader } from '@mantine/core';
import './EditProductModal.css';

interface EditProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: number;
    onSuccess?: () => void;
}

export default function EditProductModal({
    isOpen,
    onClose,
    productId,
    onSuccess,
}: EditProductModalProps) {
    const { data: categories = [] } = useCategories();
    const { data: product, isLoading: productLoading } = useProduct(productId);
    const updateProduct = useUpdateProduct();

    // Преобразуем данные продукта в initialValues для формы
    const initialValues = useMemo(() => {
        if (!product) return undefined;

        // Преобразуем categories в categorySelections
        const categorySelections: CategorySelectionItem[] = product.categories && product.categories.length > 0
            ? product.categories.map((pc) => {
                const category = pc.category as any; // Category from products/types may have sections
                const sectionId = category?.sectionId
                    || category?.sections?.[0]?.sectionId
                    || '';
                return {
                    sectionId: sectionId as number | '',
                    categoryId: pc.categoryId as number | '',
                };
            })
            : [{ sectionId: '' as const, categoryId: '' as const }];

        return {
            categorySelections,
            name: product.name || '',
            isNotFlower: product.isNotFlower ?? false,
            description: product.description || '',
            price: product.price,
            composition: Array.isArray(product.composition) && product.composition.length > 0
                ? product.composition
                : [{ value: '', count: 0 }],
            keywords: product.keywords || [],
            height: product.height || undefined,
            width: product.width || undefined,
            weight: product.weight || undefined,
            flowerCount: product.flowerCount || undefined,
            flowerTypeIds: product.flowerTypes
                ? product.flowerTypes.map(pft => pft.flowerTypeId)
                : product.flowerTypeId
                    ? [product.flowerTypeId]
                    : [],
            assemblyTypeId: product.assemblyTypeId ?? null,
            colorIds: product.colors
                ? product.colors.map(pc => pc.colorId)
                : product.colorId
                    ? [product.colorId]
                    : [],
            images: product.images?.map(img => ({
                id: img.id,
                url: img.cardUrl || img.url,
            })) || [],
        };
    }, [product, categories]);

    const handleSubmit = async (data: any) => {
        if (!product) return;

        try {
            // Преобразуем categorySelections в categoryIds
            const categoryIds = data.categorySelections
                ?.filter((selection: any) => selection.categoryId && selection.categoryId !== '')
                .map((selection: any) => Number(selection.categoryId)) || [];

            // Преобразуем массивы ID в числа (могут быть строками или числами)
            const toNumberArray = (arr: any[] | undefined): number[] | undefined => {
                if (!arr || arr.length === 0) return undefined;
                return arr.map((id) => typeof id === 'string' ? Number(id) : id).filter((id) => !isNaN(id));
            };

            // Подготавливаем данные для обновления товара
            const updateData = {
                name: data.name,
                isNotFlower: Boolean(data.isNotFlower ?? false),
                description: data.description,
                price: Number(data.price),
                categoryIds,
                composition: data.composition?.filter((item: any) => item?.value?.trim()).length
                    ? data.composition.filter((item: any) => item?.value?.trim()).map((item: any) => ({ value: String(item.value).trim(), count: Number(item.count) || 0 }))
                    : [],
                keywords: data.keywords && data.keywords.length > 0 ? data.keywords : undefined,
                height: data.height ? Number(data.height) : undefined,
                width: data.width ? Number(data.width) : undefined,
                weight: data.weight ? Number(data.weight) : undefined,
                flowerCount: data.flowerCount ? Number(data.flowerCount) : undefined,
                flowerTypeIds: toNumberArray(data.flowerTypeIds),
                assemblyTypeId: data.assemblyTypeId && data.assemblyTypeId !== null ? Number(data.assemblyTypeId) : undefined,
                colorIds: toNumberArray(data.colorIds),
            };

            // Извлекаем файлы изображений (новые файлы)
            let files: File[] | undefined;
            if (data.images) {
                // Фильтруем только новые файлы (File объекты), исключая существующие изображения (объекты с id и url)
                const newFiles = data.images.filter((img: any) => img instanceof File);
                files = newFiles.length > 0 ? newFiles : undefined;
            }

            // Определяем удаленные изображения
            // Получаем исходные ID изображений из продукта
            const originalImageIds = product.images?.map(img => img.id) || [];

            // Получаем текущие ID изображений из data.images (только те, которые не File, а объекты с id)
            const currentImageIds = data.images
                ?.filter((img: any) => !(img instanceof File) && img?.id)
                .map((img: any) => img.id) || [];

            // Находим ID изображений, которые были удалены (есть в исходных, но нет в текущих)
            const deletedImageIds = originalImageIds.filter(
                id => !currentImageIds.includes(id)
            );

            // Вызываем мутацию для обновления товара
            await updateProduct.mutateAsync({
                id: productId,
                data: updateData,
                files,
                deletedImageIds: deletedImageIds.length > 0 ? deletedImageIds : undefined
            });

            // Показываем уведомление об успехе
            notifications.show({
                title: 'Успешно',
                message: 'Товар успешно обновлен',
                color: 'green',
            });

            // Закрываем модалку
            onClose();

            // Вызываем callback успеха
            onSuccess?.();
        } catch (error: any) {
            // Получаем сообщение об ошибке
            const errorMessage = error?.response?.data?.message
                || error?.message
                || 'Произошла ошибка при обновлении товара';

            // Показываем уведомление об ошибке
            notifications.show({
                title: 'Ошибка',
                message: errorMessage,
                color: 'red',
            });
        }
    };

    const modalTitle = product
        ? `Редактировать: ${product.name}`
        : 'Редактировать товар';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            maxWidth={1200}
        >
            <div className="edit-product-modal-content">
                {productLoading ? (
                    <div className="empty-container" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                        <Loader />
                    </div>
                ) : (
                    product && (
                        <ProductForm
                            onSubmit={handleSubmit}
                            initialValues={initialValues}
                            isEdit={true}
                            isLoading={updateProduct.isPending}
                        />
                    )
                )}
            </div>
        </Modal>
    );
}

